import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  FileText,
  Presentation as PresentationIcon,
  Settings,
  Building,
  Shield,
  ChevronLeft,
  ChevronRight,
  Home,
  MessageSquare,
  Library
} from 'lucide-react'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  onToggle: () => void
}

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  roles: string[]
  badge?: string
}

export function Sidebar({ isOpen, onClose, onToggle }: SidebarProps) {
  const location = useLocation()
  const [user, setUser] = useState<{ name: string; email: string; role: string } | null>(null)

  useEffect(() => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        setUser(JSON.parse(userStr))
      } catch (error) {
        console.error('Failed to parse user data:', error)
      }
    }
  }, [])

  const navigation: NavItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['demo', 'company_user', 'company_admin', 'super_admin']
    },
    {
      name: 'Public Home',
      href: '/',
      icon: Home,
      roles: ['demo', 'company_user', 'company_admin', 'super_admin'],
      badge: 'Public'
    },
    {
      name: 'Company Profile',
      href: '/company/profile',
      icon: Building,
      roles: ['company_admin', 'super_admin']
    },
    {
      name: 'Users',
      href: '/users',
      icon: Users,
      roles: ['company_admin', 'super_admin']
    },
    {
      name: 'Content',
      href: '/content',
      icon: FileText,
      roles: ['company_admin', 'super_admin']
    },
    {
      name: 'Library',
      href: '/library',
      icon: Library,
      roles: ['demo', 'company_user', 'company_admin', 'super_admin']
    },
    {
      name: 'Presentations',
      href: '/presentations',
      icon: PresentationIcon,
      roles: ['demo', 'company_user', 'company_admin', 'super_admin']
    },
    {
      name: 'AI Assistant',
      href: '/chat',
      icon: MessageSquare,
      roles: ['demo', 'company_user', 'company_admin', 'super_admin']
    },
    {
      name: 'Companies',
      href: '/companies',
      icon: Building,
      roles: ['super_admin']
    },
    {
      name: 'Admin Panel',
      href: '/admin',
      icon: Shield,
      roles: ['super_admin']
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      roles: ['company_user', 'company_admin', 'super_admin']
    }
  ]

  // Filter navigation items based on user role
  const filteredNav = user
    ? navigation.filter((item) => item.roles.includes(user.role))
    : []

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border shadow-xl
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Toggle button on right edge - middle of sidebar */}
        <button
          onClick={onToggle}
          className="absolute top-1/2 -translate-y-1/2 -right-3 lg:flex hidden w-6 h-6 bg-card border border-border rounded-md items-center justify-center shadow-md hover:bg-muted transition-colors z-50"
          aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isOpen ? (
            <ChevronLeft className="w-4 h-4 text-orange-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-orange-500" />
          )}
        </button>

        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
              <span className="text-white font-bold text-sm">CS</span>
            </div>
            <span className="font-semibold text-lg text-foreground">securesight.ai</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredNav.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')

            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => {
                  // Close mobile sidebar after navigation
                  if (window.innerWidth < 1024) {
                    onClose()
                  }
                }}
                className={({ isActive: navIsActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-200 relative
                  ${navIsActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                    {item.badge}
                  </span>
                )}
                {isActive && !isOpen && (
                  <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </NavLink>
            )
          })}
        </nav>

      </aside>
    </>
  )
}
